import React, { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import bash from 'react-syntax-highlighter/dist/cjs/languages/prism/bash';
import go from 'react-syntax-highlighter/dist/cjs/languages/prism/go';
import java from 'react-syntax-highlighter/dist/cjs/languages/prism/java';
import javascript from 'react-syntax-highlighter/dist/cjs/languages/prism/javascript';
import json from 'react-syntax-highlighter/dist/cjs/languages/prism/json';
import python from 'react-syntax-highlighter/dist/cjs/languages/prism/python';
import ruby from 'react-syntax-highlighter/dist/cjs/languages/prism/ruby';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';

import styles from './ChatMarkdown.module.scss';

interface ChatMarkdownProps {
  body: string;
}

// Register syntax highlighting languages
// This is done outside the component to avoid re-registering on each render
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('ruby', ruby);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('bash', bash);

const ChatMarkdown: FC<ChatMarkdownProps> = ({ body }) => {
  const syntaxTheme = vscDarkPlus;

  // Define interfaces for ReactMarkdown component props
  interface CodeProps {
    className?: string;
    children: React.ReactNode;
    [key: string]: any;
  }

  const MarkdownComponents = {
    code({ className, children, ...props }: CodeProps) {
      const hasLang = /language-(\w+)/.exec(className || '');
      const content = String(children).replace(/\n$/, '');

      return hasLang ? (
        <SyntaxHighlighter
          style={syntaxTheme}
          language={hasLang[1]}
          PreTag='div'
          className={styles.highlightedCode}
          showLineNumbers={true}
          wrapLines={true}
          useInlineStyles={true}
        >
          {content}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {content}
        </code>
      );
    },
  };

  return (
    <div className={styles.chatMarkdown}>
      <ReactMarkdown
        linkTarget='_blank'
        components={MarkdownComponents}
        remarkPlugins={[remarkGfm]}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
};

ChatMarkdown.displayName = 'ChatMarkdown';
export default ChatMarkdown;
